import React, { useContext, useMemo } from 'react';
import { UserContext } from '../../Contexts/UserContext';
import { ACCESS_CONTROL_CONSTANTS } from '../../core/constants';
import GeneralIconButton from '../GeneralIconButton';

//mui imports====
import { Box } from '@mui/system';
import useStyles from './styles';
import { Grid, TextField, Typography } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
//===============

const { SYSTEMS, UI_ACCESS, TYPES } = ACCESS_CONTROL_CONSTANTS;

// This Component can be used to display an array which has multiple inputs. Inputs can be added, updated and deleted.
const ArrayInputConfig = ({
	keyData,
	object,
	updateObject,
	isObjectEditor = true,
	directValue,
	updateDirectValue,
	label,
	inputContainerStyle,
	inputGridStyle,
	entireArrayEditable,
	isMandatory = false,
	system = SYSTEMS.FRAMERATE
}) => {
	const classes = useStyles();
	const { getAccessControlSet } = useContext(UserContext);

	const inputArray = (isObjectEditor ? object[keyData] : directValue) || [];
	const displayLabel = isMandatory ? `${label} *` : label;
	/**
	 * @name addNewElement
	 * @description This function can be used to add a new element to array, with two scenarios if isObjectEditor is true or false
	 * 1. true : add a new element to aray with the rawConfig functionality and passes value to call back function
	 * 2. false : add a new element to aray with  direct value functionality and passes it to the callback function
	 */
	const addNewElement = () => {
		let deepCloneArray = structuredClone(inputArray);
		let updatedArray = [...deepCloneArray, ''];
		if (isObjectEditor && object) {
			let rawObject = structuredClone(object);
			updateObject({ ...rawObject, [keyData]: updatedArray });
		} else if (directValue) {
			updateDirectValue(updatedArray);
		} else alert("Could'nt find inputArray in rawConfig!");
	};

	/**
	 * @name updateArrayElement
	 * @description This function can be used to update an element in the array, with two scenarios if the isObjectEditor is true or false
	 * 1. true : updated array element with the rawConfig functionality and passes value to call back function
	 * 2. false : updated array element with  direct value functionality and passes it to the callback function
	 * @param {number} id - it is the id of the particular array element which is to be updated
	 * @param {string} updatedValue - it is the updated value  which is to be updated over the particular array element
	 */
	const updateArrayElement = (id, upatedValue) => {
		let updatedArray;
		if (isObjectEditor && object && object[keyData] && Array.isArray(object[keyData])) {
			let rawObject = structuredClone(object);
			updatedArray = structuredClone(object[keyData]);
			updatedArray[id] = upatedValue;
			updateObject({ ...rawObject, [keyData]: updatedArray });
		} else if (directValue) {
			updatedArray = structuredClone(directValue);
			updatedArray[id] = upatedValue;
			updateDirectValue(updatedArray);
		} else alert("Could'nt find inputArray in rawConfig!");
	};

	/**
	 * @name deleteArrayElement
	 * @description This function can be used to delete an element from the array, with two scenarios if the isObjectEditor is true or false
	 * 1. true : deletes an array element with the rawConfig functionality and passes value to call back function
	 * 2. false : deletes an array element with  direct value functionality and passes it to the callback function
	 * @param {number} idx - it is the id of the particular array element which is to be deleted
	 */
	const deleteArrayElement = (idx) => {
		let updatedArray;
		if (isObjectEditor && object && object[keyData] && Array.isArray(object[keyData])) {
			let rawObject = structuredClone(object);
			updatedArray = structuredClone(object[keyData]);
			updatedArray = updatedArray.filter((data, id) => id !== idx);

			updateObject({ ...rawObject, [keyData]: updatedArray });
		} else if (directValue) {
			updatedArray = structuredClone(directValue);
			updatedArray = updatedArray.filter((data, id) => id !== idx);
			updateDirectValue(updatedArray);
		} else alert("Could'nt find inputArray in rawConfig!");
	};

	/**
	 * This variable contains a set of strings that signifies if this button is either visible/disabled/hidden
	 * @type {Set<string>}
	 */
	const arrayInputConfigControlledAccessSet = useMemo(() => {
		return getAccessControlSet(system, TYPES.INPUT_NAMES, keyData);
	}, [getAccessControlSet, system, keyData]);

	return (
		<>
			{!arrayInputConfigControlledAccessSet.has(UI_ACCESS.HIDE) && (
				<>
					<Box sx={{ marginTop: '3rem', display: 'flex' }} spacing={4}>
						<Typography fontWeight={600} variant="h5">
							{displayLabel}
						</Typography>

						<GeneralIconButton
							color="primary"
							fontSize="large"
							className={classes.icon}
							onClick={addNewElement}
							style={{ marginLeft: '1rem', marginTop: '-.5rem' }}
							name={keyData + '.ADD_NEW_ELEMENT_BUTTON'}
						>
							<AddCircleOutlineOutlinedIcon />
						</GeneralIconButton>
					</Box>
					<Grid style={inputContainerStyle || {}}>
						{inputArray.map((arrayElement, id) => {
							return (
								<SingleArrayElement
									key={id}
									id={id}
									arrayElement={arrayElement}
									updateArrayElement={updateArrayElement}
									deleteArrayElement={deleteArrayElement}
									editable={entireArrayEditable}
									inputGridStyle={inputGridStyle || {}}
									keyData={keyData + `.${id}`}
								/>
							);
						})}
					</Grid>
				</>
			)}
		</>
	);
};

const SingleArrayElement = ({
	id,
	arrayElement,
	updateArrayElement,
	deleteArrayElement,
	editable,
	inputGridStyle,
	keyData
}) => {
	const classes = useStyles();
	return (
		<Grid item xs={4} style={inputGridStyle}>
			<TextField
				id="outlined-size-normal-arrayElement"
				style={{ width: '80%', margin: '1rem 0' }}
				value={arrayElement || ''}
				onChange={(e) => {
					updateArrayElement(id, e.target.value);
				}}
				disabled={editable === false}
			/>

			<Box style={{ display: 'flex', alignItems: 'center', marginLeft: '.5rem' }}>
				<GeneralIconButton
					color="primary"
					onClick={() => deleteArrayElement(id)}
					className={classes.icon}
					name={keyData + '.DELETE_ELEMENT_BUTTON'}
				>
					<DeleteOutlinedIcon />
				</GeneralIconButton>
			</Box>
		</Grid>
	);
};

export default ArrayInputConfig;
